import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import useViewport from '@hooks/useViewport';
import useWindowWidth from '@hooks/useWindowWidth';
import { cardLineStyle } from '@styles/common/Card';
import { scrollbarStyle } from '@styles/variants/scrollbar';

const mobileStyle = css`
  position: fixed;
  left: 0;
  right: 0;
  top: 50px;
  width: 100% !important;
  max-height: fit-content;
  overflow-y: auto;
`;

const DropdownWrap = styled(motion.div) <{
  direction: keyof typeof dropdownDirMap;
  mobileFilled?: boolean;
}>`
  position: absolute;
  display: flex;
  flex-direction: column;
  text-align: left;

  min-width: 150px;
  width: auto;
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);
  background-color: var(--cardBackgroundColor);
  padding: 8px;
  z-index: 2;
  ${cardLineStyle}
  ${scrollbarStyle}

  ${({ direction }) => direction && dropdownDirMap[direction]}

  @media (max-width: 480px) {
    ${({ mobileFilled }) => mobileFilled && mobileStyle}
  }
`;

export const topToBottomAni = {
  enter: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1],
    },
    display: 'block',
    transform: 'scaleY(1)',
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: [0.755, 0.05, 0.855, 0.06],
    },
    transitionEnd: {
      display: 'none',
    },
    transform: 'scaleY(0.8)',
  },
};

export const dropdownDirMap = {
  topRight: css`
    top: 100%;
    right: 0;
  `,
  topLeft: css`
    top: 100%;
    left: 0;
  `,
  bottomRight: css`
    bottom: 100%;
    right: 0;
  `,
  bottomLeft: css`
    bottom: 100%;
    left: 0;
  `,
  leftTop: css`
    left: 100%;
    bottom: 0;
  `,
  rightTop: css`
    right: 100%;
    bottom: 0;
  `,
  rightBottom: css`
    right: 100%;
    top: 0;
  `,
  leftBottom: css`
    left: 100%;
    top: 0;
  `,
  bottom: css`
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
  `,
  top: css`
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
  `,
};

export const dirType = {
  TOPRIGHT: 'topRight',
  TOPLEFT: 'topLeft',
  LEFTTOP: 'leftTop',
  RIGHTTOP: 'rightTop',
  RIGHTBOTTOM: 'rightBottom',
  LEFTBOTTOM: 'leftBottom',
  BOTTOMRIGHT: 'bottomRight',
  BOTTOMLEFT: 'bottomLeft',
  BOTTOM: 'bottom',
  TOP: 'top',
};

type Props = {
  direction?: keyof typeof dropdownDirMap | undefined;
  open?: boolean;
  handleClickOutside?: (e: any, dropdownNode) => void;
  children: any;
  style?: any;
  maxHeight?: number | string;
  className?: string;
  mobileFilled?: boolean;
  mobileFull?: boolean;
  anchorRef?: React.RefObject<HTMLElement>;
  dropdownWidth?: number;
  /** true이면 document.body에 Portal로 렌더링하여 overflow 잘림 방지 */
  usePortal?: boolean;
  /** usePortal 사용 시 위치 기준이 되는 트리거 요소 ref */
  triggerRef?: React.RefObject<HTMLElement>;
};

function DropdownWrapper({
  direction = 'topLeft',
  open,
  handleClickOutside,
  children,
  style,
  maxHeight,
  className,
  mobileFilled,
  anchorRef,
  dropdownWidth,
  usePortal,
  triggerRef,
  ...props
}: Props) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { viewWidth, viewHeight } = useViewport();
  const resizeWidth = useWindowWidth();
  const [fixedPos, setFixedPos] = useState<{
    left: number;
    top: number;
  } | null>(null);

  // ── Portal 모드: triggerRef 기준 fixed 위치 계산 ──
  const [portalPos, setPortalPos] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!usePortal || !open || !triggerRef?.current) return;

    const compute = () => {
      const triggerEl = triggerRef.current;
      if (!triggerEl) return;
      const rect = triggerEl.getBoundingClientRect();
      const dropEl = dropdownRef.current;

      // direction에 따라 위/아래 결정
      const isBottom =
        direction === 'bottomLeft' ||
        direction === 'bottomRight' ||
        direction === 'bottom';

      let top: number;
      if (isBottom && dropEl) {
        // 위로 열림: 트리거 상단 - 드롭다운 높이
        top = rect.top - dropEl.offsetHeight;
      } else {
        // 아래로 열림: 트리거 하단
        top = rect.bottom;
      }

      // direction에 따라 좌/우 결정
      const isRight =
        direction === 'topRight' || direction === 'bottomRight';
      let left: number;
      if (isRight) {
        left = rect.right - (dropEl?.offsetWidth ?? rect.width);
      } else {
        left = rect.left;
      }

      setPortalPos({ left, top, width: rect.width });
    };

    compute();
    // 첫 렌더 후 dropdownRef가 마운트되면 재계산
    requestAnimationFrame(compute);

    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [usePortal, open, triggerRef, direction, resizeWidth]);

  // --------------| handleClickOutside 예시 |----------------
  //
  // dropdown 바깥을 누르면 dropdown이 닫힘
  //
  // function handleClickOutside(e: any, dropdownNode) {
  //   if (targetRef.current && isUserOpen && dropdownNode) {
  //     const isClickOutside =
  //       !targetRef.current.contains(e.target) &&
  //       !dropdownNode.contains(e.target);

  //     if (isClickOutside) {
  //       setIsUserOpen(false);
  //     }
  //   }
  // }
  // --------------------------------------------------------

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', (e) => {
        if (handleClickOutside) handleClickOutside(e, dropdownRef.current);
      });
    }

    return () => {
      document.removeEventListener('mousedown', (e) => {
        if (handleClickOutside) handleClickOutside(e, dropdownRef.current);
      });
    };
  }, [open, handleClickOutside]);

  // Center to anchor for top/bottom when anchorRef provided
  useLayoutEffect(() => {
    if (!open) return;
    if (!anchorRef?.current) {
      setFixedPos(null);
      return;
    }
    if (!(direction === 'top' || direction === 'bottom')) {
      setFixedPos(null);
      return;
    }
    const compute = () => {
      const anchorEl = anchorRef.current!;
      const dropEl = dropdownRef.current;
      const aRect = anchorEl.getBoundingClientRect();
      // Temporarily ensure dropdown is measurable
      if (dropEl) {
        // Make visible for measuring if needed
        const prevDisplay = dropEl.style.display;
        if (prevDisplay === 'none') dropEl.style.display = 'block';
        const dRect = dropEl.getBoundingClientRect();
        const left = Math.round(aRect.left + aRect.width / 2 - dRect.width / 2);
        const top =
          direction === 'top'
            ? Math.round(aRect.bottom)
            : Math.round(aRect.top - dRect.height);
        setFixedPos({ left, top });
        // revert
        dropEl.style.display = prevDisplay;
      }
    };
    compute();
    const onScroll = () => compute();
    const onResize = () => compute();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, anchorRef, direction, resizeWidth]);

  const dropdownContent = (
    <DropdownWrap
      ref={dropdownRef}
      className={className}
      direction={direction}
      initial="false"
      animate="enter"
      exit="exit"
      variants={topToBottomAni}
      style={{
        transformOrigin:
          direction === dirType.TOPLEFT ||
            direction === dirType.TOPRIGHT ||
            direction === dirType.RIGHTBOTTOM ||
            direction === dirType.LEFTBOTTOM
            ? 'top'
            : 'bottom',
        // ── Portal 모드: fixed 위치 직접 지정 ──
        ...(usePortal && portalPos
          ? {
            position: 'fixed' as const,
            left: portalPos.left,
            top: portalPos.top,
            zIndex: 9999,
            minWidth: portalPos.width,
            width: portalPos.width,
          }
          : {}),
        // When anchored and top/bottom, use fixed positioning centered to trigger
        ...(!usePortal && anchorRef &&
          (direction === 'top' || direction === 'bottom') &&
          fixedPos
          ? ({
            position: 'fixed',
            left: fixedPos.left,
            top: fixedPos.top,
          } as any)
          : {}),
        // Centering by provided width (or style.width) when top/bottom without anchor
        ...(!usePortal && !anchorRef && (direction === 'top' || direction === 'bottom')
          ? (() => {
            const styleWidth = style?.width;
            const parsed =
              typeof dropdownWidth === 'number'
                ? dropdownWidth
                : typeof styleWidth === 'number'
                  ? styleWidth
                  : typeof styleWidth === 'string' && /\d+/.test(styleWidth)
                    ? parseInt(styleWidth, 10)
                    : undefined;
            if (parsed && Number.isFinite(parsed)) {
              return {
                left: `calc(50% - ${parsed / 2}px)`,
                width: styleWidth ? styleWidth : `${parsed}px`,
              } as any;
            }
            return {} as any;
          })()
          : {}),
        maxHeight,
        ...style,
      }}
      mobileFilled={mobileFilled}
    >
      {children}
    </DropdownWrap>
  );

  if (usePortal) {
    return createPortal(dropdownContent, document.body);
  }

  return dropdownContent;
}

export default DropdownWrapper;
